import { ImagesVisualizer, NotFound } from "shared-components";

import { ProductDeliveryPeriod } from "@/components/product/DeliveryPeriod";
import { ProductDescription } from "@/components/product/Description";
import { ProductItemSelector } from "@/components/product/ItemSelector";
import { ProductReportModal } from "@/components/product/ReportModal";
import { useProductStoreBreadcrumb } from "@/hooks/use-product-store-breadcrumb";
import { useProductPage } from "@/hooks/use-product-page";
import { useAuthGuard } from "@/hooks/use-auth-guard";

import type { JSX } from "react";
import { ProductGroup } from "@/components/product/ProductGroup";
import clsx from "clsx";

export const ProductPage = ({
  storefront = false,
  storeAlias,
}: { storefront?: boolean; storeAlias?: string }): JSX.Element => {
  const {
    productId,
    productInfo,
    colorOptions,
    customizationDetailsForViewer,
    imageUrls,
    blankImages,
    preloadBlankImages,
    sizes,
    productItemValues,
    setProductItemValues,
    isLoading,
    loadingAddToCart,
    loadingAddToWishlist,
    onAddToCart,
    onFavorite,
    isFavorited,
    errors,
    similarProducts,
    isLoadingSimilarProducts,
    storeProducts,
    isLoadingStoreProducts,
  } = useProductPage(storeAlias);

  const { isAuthenticated } = useAuthGuard();

  useProductStoreBreadcrumb({ productName: productInfo.name });

  const INSTALLMENTS_QUANTITY = 12;

  const calculateInstallmentValue = (price: number, installments: number): number => {
    const numericPrice = Number(price);
    if (!numericPrice || numericPrice <= 0 || !Number.isFinite(numericPrice)) {
      return 0;
    }
    return numericPrice / installments;
  };

  if (errors.statusCode === 404) {
    return <NotFound />;
  }

  return (
    <div
      className={clsx([
        "mb-[8.75rem] flex w-full flex-col justify-center px-page-x-margin text-brand-primary lg:mb-[9.75rem]",
        { "mt-6": storefront },
      ])}
    >
      <section className="flex w-full max-w-page-max-width max-lg:flex-col max-lg:items-center lg:gap-3">
        <div className="w-full">
          <ImagesVisualizer
            customizationDetails={customizationDetailsForViewer}
            imageUrls={imageUrls}
            blankImages={blankImages}
            preloadCandidates={preloadBlankImages}
            preferLightweightImages={true}
            loading={isLoading || loadingAddToWishlist}
            onFavorite={() => isAuthenticated() && onFavorite()}
            hideFavorite={storefront}
            isFavorited={isFavorited}
            favoritesLoading={loadingAddToWishlist}
          />

          <div className="mt-3 max-w-[50.5rem] max-lg:hidden">
            <ProductDescription
              themes={productInfo.themes}
              description={productInfo.description}
              technicalData={productInfo.technicalSheet}
              loading={isLoading}
            />

            <ProductReportModal className="mt-3" productId={productId as string} />
          </div>
        </div>

        <div
          className={
            "h-fit w-full min-w-[18rem] max-w-[34rem] rounded-lg lg:max-w-[26rem] lg:p-2 " +
            "lg:ml-auto lg:outline lg:outline-2 lg:outline-base-input"
          }
        >
          <ProductItemSelector
            className="max-lg:mt-4"
            printName={productInfo.printName || ""}
            name={productInfo.name || ""}
            store={productInfo.store}
            price={+(productInfo.price || 0)}
            cardPaymentSettings={{
              installments: INSTALLMENTS_QUANTITY,
              value: calculateInstallmentValue(productInfo.price, INSTALLMENTS_QUANTITY),
            }}
            colors={colorOptions}
            sizes={sizes}
            values={productItemValues}
            setValues={setProductItemValues}
            loading={isLoading || loadingAddToCart}
            errors={errors.itemValues}
            onAddToCart={onAddToCart}
            storefront={storefront}
          />

          <ProductDeliveryPeriod
            className="mt-4 lg:mt-2"
            productId={productId as string}
            amount={productItemValues.quantity}
          />
        </div>

        <div className="mt-2 w-full max-w-[34rem] lg:hidden">
          <ProductDescription
            themes={productInfo.themes || []}
            description={productInfo.description || ""}
            technicalData={productInfo.technicalSheet || []}
            loading={isLoading}
          />

          <ProductReportModal className="mt-3" productId={productId as string} />
        </div>
      </section>

      <section className="mt-6 w-full max-w-page-max-width flex flex-col gap-y-10">
        {!storefront && (
          <ProductGroup
            id="similar-products"
            title="Gostou desse produto? Veja peças similares"
            products={similarProducts}
            loading={isLoadingSimilarProducts}
          />
        )}

        <ProductGroup
          id="store-products"
          title={storefront ? "Você também pode gostar" : "Produtos da mesma loja"}
          products={storeProducts}
          loading={isLoadingStoreProducts}
        />
      </section>
    </div>
  );
};
